import type { Dispatch } from 'redux';
import type { DocumentType } from '../components/views/document-view/document-viewer.ts';
import type { Page } from '../components/views/settings-view/types';
import type { Route } from '../routes.ts';
import type { ReduxState } from './types';
import type { ImageQueuePatientStatus } from '../types/Image';
import type { Patient } from '../types/Patient';
import type { Config, Settings, SystemSettings } from '../types/Settings';
import type { UserInfo } from '../types/UserInfo';
import { getStore } from './store/getStore.ts';
import { createAction } from './utils/createAction.ts';
import { createActionBuilder } from './utils/createActionBuilder.ts';
import { createReducerBuilder } from './utils/createReducerBuilder.ts';
import { createSelector } from './utils/createSelector.ts';
import type { RouteParams, RouteQuery } from '../utils/router/Router.ts';

export interface AppReduxState {
  patientId?: string;
  showOverlay?: boolean;
  page?: Route;
  params?: RouteParams;
  query?: RouteQuery;
  authReady?: boolean;
  authGranted?: boolean;
  config?: Config;
  settings?: Settings;
  settingsLoaded?: boolean;
  imageQueuePatientStatus?: ImageQueuePatientStatus;
  me?: UserInfo;
  patient?: Patient;
  systemSettings?: SystemSettings;
  verifyDisabled?: boolean;
}

export const AppAction = {
  UpdateConfig: 'UpdateConfig',
  UpdateMe: 'UpdateMe',
  UpdateSettings: 'UpdateSettings',
  UpdateImageQueuePatientStatus: 'UpdateImageQueuePatientStatus',
  UpdatePage: 'UpdatePage',
  UpdatePatient: 'UpdatePatient',
  UpdatePatientId: 'UpdatePatientId',
  UpdateSystemSettings: 'UpdateSystemSettings',
  ShowOverlay: 'ShowOverlay',
  VerifyDisabled: 'VerifyDisabled',
  AuthorizePage: 'AuthorizePage'
} as const;

export type AppAction = ValueOf<typeof AppAction>;

interface UpdatePageAction {
  page: Route;
  params: RouteParams;
  query: RouteQuery;
  requiresAuth: boolean;
}

interface AuthorizePageAction {
  page: Route;
  granted: boolean;
}

export const {
  app, configSelector, meSelector, settingsSelector, systemSettingsSelector, settingsLoadedSelector, settingsPageSelector, organizationSelector,
  documentIdSelector, documentTypeSelector, updateSystemSettings, updateSettings, updateMe, updatePatientId, updatePatient, patientSelector, patientIdSelector,
  disableVerify, enableVerify, verifyDisabled, imageQueuePatientStatusSelector, updateImageQueuePatientStatus, patientSerSelector, pageSelector,
  showOverlay, showOverlaySelector, hideOverlay, authReadySelector, authGrantedSelector, updateConfig, authorize, navigate
} = createReducerBuilder<AppAction, 'app', AppReduxState>('app')
  // direct values
  .add(AppAction.UpdateConfig, 'config', undefined as (Config | undefined))
  .add(AppAction.UpdateMe, 'me', undefined as (UserInfo | undefined))
  .add(AppAction.UpdateImageQueuePatientStatus, 'imageQueuePatientStatus', undefined as (ImageQueuePatientStatus | undefined))
  .add(AppAction.UpdateSystemSettings, 'systemSettings', undefined as (SystemSettings | undefined))
  .add(AppAction.UpdatePatient, 'patient', undefined as (Patient | undefined))

  // settings
  .addAction('updateSettings', createActionBuilder(AppAction.UpdateSettings, (settings: Settings | undefined) => ({ settings })))
  .addSelector('settingsSelector', createSelector<Settings>('app.settings'))
  .addSelector('settingsLoadedSelector', createSelector<boolean>('app.settingsLoaded'))
  .addReducer(AppAction.UpdateSettings, (state: AppReduxState, { settings }: { settings: Settings }) =>
    ({ ...state, settings, settingsLoaded: true }))


  // patient id
  .addAction('updatePatientId', createActionBuilder(AppAction.UpdatePatientId, (patientId: string) => ({ patientId })))
  .addSelector('patientIdSelector', createSelector<string>(['app.patientId', 'app.patient.id', 'app.params.patient']))
  .addReducer(AppAction.UpdatePatientId, (state: AppReduxState, { patientId } : { patientId: string }): AppReduxState =>
    ({ ...state, patientId }))

  // verify disabled
  .addAction('disableVerify', createActionBuilder(AppAction.VerifyDisabled, () => ({ disabled: true })))
  .addAction('enableVerify', createActionBuilder(AppAction.VerifyDisabled, () => ({ disabled: false })))
  .addSelector('verifyDisabled', createSelector<boolean>('app.verifyDisabled'))
  .addReducer(AppAction.VerifyDisabled, (state: AppReduxState, { disabled }: { disabled: boolean }): AppReduxState =>
    ({ ...state, verifyDisabled: disabled }))

  // show/hide overlay
  .addAction('showOverlay', createActionBuilder(AppAction.ShowOverlay, () => ({ show: true })))
  .addAction('hideOverlay', createActionBuilder(AppAction.ShowOverlay, () => ({ show: false })))
  .addSelector('showOverlaySelector', createSelector<boolean>('app.showOverlay', false))
  .addReducer(AppAction.ShowOverlay, (state: AppReduxState, { show }: { show: boolean }): AppReduxState =>
    ({ ...state, showOverlay: show }))

  // navigate
  .addAction('navigate', createAction((page: Route, loadFile: MaybePromiseFunction | undefined,
    params: RouteParams, query: RouteQuery, requiresAuth: boolean) =>
    async (dispatch: Dispatch<UpdatePageAction & { type: string }>) => {
      dispatch({ type: AppAction.UpdatePage, page, params, query, requiresAuth });
      await loadFile?.();
    }
  ))
  .addReducer(AppAction.UpdatePage, (state: AppReduxState, { page, params, query, requiresAuth }: UpdatePageAction): AppReduxState =>
    ({ ...state, page, params, query, authReady: !requiresAuth, authGranted: requiresAuth ? undefined : true }))

  // authorize
  .addAction('authorize', createActionBuilder(AppAction.AuthorizePage, (page: Route, granted: boolean) => ({ page, granted })))
  .addReducer(AppAction.AuthorizePage, (state: AppReduxState, { page, granted }: AuthorizePageAction): AppReduxState =>
    page === state.page
      ? ({ ...state, authReady: true, authGranted: Boolean(granted) })
      : state
  )

  // other selectors
  .addSelector('pageSelector', createSelector<Route>('app.page'))
  .addSelector('organizationSelector', createSelector<string>('app.params.organization'))
  .addSelector('patientSerSelector', createSelector<string | number, int>(
    ['app.params.patientSer', 'app.patient.ser', 'app.params.ser'],
    undefined,
    v => parseInt(`${v}`, 10)
  ))
  .addSelector('documentIdSelector', createSelector<string>('app.params.documentId'))
  .addSelector('documentTypeSelector', createSelector<DocumentType>('app.params.documentType'))
  .addSelector('settingsPageSelector', createSelector<Page['name']>('app.params.page', 'main'))
  .addSelector('authGrantedSelector', createSelector<boolean>('app.authGranted', undefined))
  .addSelector('authReadySelector', createSelector<boolean>('app.authReady', false))
  .build();

export function paramSelector(param: string, defaultValue?: string): (state?: ReduxState) => string | undefined {
  return createSelector<string>(`app.params.${param}`, defaultValue);
}

/** Gets the most applicable organization ID basesd on the current state. */
export function organizationIdSelector(state: ReduxState = getStore().getState()): string | undefined {
  const me = meSelector(state);
  const { id } = ((me || { organizations: [] })
    .organizations.find(({ slug }) => organizationSelector(state) === slug) || {});

  if (id) return id;

  if (me) return me.organizations[0].id;
}

/**
 * Gets the most applicable organization slug based on the current state.
 */
export function organizationSlugSelector(state: ReduxState = getStore().getState()): string | undefined {
  const slug = organizationSelector(state);

  if (slug) return slug;

  const me = meSelector(state);

  if (me) return me.organizations[0].slug;
}